import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { firstAccessible } from "@/lib/nav";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import MasterSKU from "@/pages/MasterSKU";
import ReturnToRack from "@/pages/ReturnToRack";
import AdjustLog from "@/pages/AdjustLog";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Finance from "@/pages/Finance";
import { WarehouseArrivalDetail } from "@/pages/WarehouseArrivals";
import Inbound from "@/pages/Inbound";
import CostAnalysis from "@/pages/CostAnalysis";
import Integrations from "@/pages/Integrations";
import HR from "@/pages/HR";
import MySchedule from "@/pages/MySchedule";
import MyAttendance from "@/pages/MyAttendance";
import MyLeave from "@/pages/MyLeave";
import MyTasks from "@/pages/MyTasks";
import TaskAdmin from "@/pages/TaskAdmin";
import AttendanceQrDisplay from "@/pages/AttendanceQrDisplay";
import PackingRoster from "@/pages/PackingRoster";
import Users from "@/pages/Users";
import MarketplaceOrders from "@/pages/MarketplaceOrders";
import MarketplaceConnections from "@/pages/MarketplaceConnections";
import MarketplaceSkuMapping from "@/pages/MarketplaceSkuMapping";
import MarketplaceProducts from "@/pages/MarketplaceProducts";
import MarketplaceReports from "@/pages/MarketplaceReports";
import MarketplaceErpReads from "@/pages/MarketplaceErpReads";

function Protected({ children, perm, anyPerm }) {
  const { user, loading, can } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const ok = perm ? can(perm) : anyPerm ? anyPerm.some(can) : true;
  if (!ok) { const dest = firstAccessible(can); return <Navigate to={dest || "/no-access"} replace />; }
  return children;
}

function Landing() {
  const { can } = useAuth();
  const dest = firstAccessible(can);
  return <Navigate to={dest || "/no-access"} replace />;
}

function NoAccess() {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen grid place-items-center text-center p-8" data-testid="no-access-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-800">Access Denied</h1>
        <p className="text-slate-500 mt-2 text-sm">Your role has no accessible pages. Contact an administrator.</p>
        <button onClick={logout} className="mt-4 text-sky-600 underline text-sm">Sign out</button>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/hr/roster" element={<Protected perm="hr:schedule_view"><PackingRoster /></Protected>} />
      <Route path="/attendance-qr" element={<Protected perm="attendance:qr_display"><AttendanceQrDisplay /></Protected>} />
      <Route path="/" element={<Protected><Layout /></Protected>}>
        <Route index element={<Landing />} />
        <Route path="no-access" element={<NoAccess />} />
        <Route path="dashboard" element={<Protected perm="dashboard:view"><Dashboard /></Protected>} />
        <Route path="skus" element={<Protected perm="sku:view"><MasterSKU /></Protected>} />
        <Route path="return-to-rack" element={<Protected perm="sku:view"><ReturnToRack /></Protected>} />
        <Route path="adjust-log" element={<Protected perm="system:adjust_log"><AdjustLog /></Protected>} />
        <Route path="finance" element={<Protected anyPerm={["invoice:view","freight:view","bukku:view","bukku:logs","system:shipping_rates","system:billing_companies"]}><Finance /></Protected>} />
        <Route path="invoices" element={<Navigate to="/finance?tab=invoices" replace />} />
        <Route path="invoices/:id" element={<Protected perm="invoice:view_details"><InvoiceDetail /></Protected>} />
        <Route path="inbound" element={<Protected anyPerm={["transit:view","arrival:pending_view","arrival:check_view","arrival:checked_view"]}><Inbound /></Protected>} />
        <Route path="china-tracking" element={<Navigate to="/inbound" replace />} />
        <Route path="warehouse-cbm" element={<Navigate to="/inbound" replace />} />
        <Route path="arrivals" element={<Navigate to="/inbound" replace />} />
        <Route path="arrivals/print" element={<Protected anyPerm={["arrival:check_view","arrival:checked_view","arrival:pending_view","print:packing_list"]}><WarehouseArrivalDetail /></Protected>} />
        <Route path="arrivals/:code" element={<Protected anyPerm={["arrival:check_view","arrival:checked_view","arrival:pending_view","print:packing_list"]}><WarehouseArrivalDetail /></Protected>} />
        <Route path="shipping-rates" element={<Navigate to="/finance?tab=setup" replace />} />
        <Route path="billing-companies" element={<Navigate to="/finance?tab=setup" replace />} />
        <Route path="freight-invoices" element={<Navigate to="/finance?tab=freight" replace />} />
        <Route path="bukku-products" element={<Navigate to="/finance?tab=bukku" replace />} />
        <Route path="bukku-logs" element={<Navigate to="/finance?tab=bukku" replace />} />
        <Route path="cost-analysis" element={<Protected perm="system:cost_analysis"><CostAnalysis /></Protected>} />
        <Route path="integrations" element={<Protected perm="integration:view"><Integrations /></Protected>} />
        <Route path="hr" element={<Protected anyPerm={["hr:employee_view","hr:import_autocount","hr:schedule_view","payroll_history:view","attendance:view_team","attendance:view_all","leave:view_team","leave:view_all","leave:approve","leave:balance_manage","leave:type_manage","leave:import_autocount","payroll:view","payroll:process","payroll:review","payroll:finalize","payroll:manage_profiles","payroll:adjust","payroll:statutory_manage"]}><HR /></Protected>} />
        <Route path="my-schedule" element={<Protected perm="schedule:view_own"><MySchedule /></Protected>} />
        <Route path="my-attendance" element={<Protected perm="attendance:view_own"><MyAttendance /></Protected>} />
        <Route path="my-leave" element={<Protected perm="leave:view_own"><MyLeave /></Protected>} />
        <Route path="tasks" element={<Protected perm="task:view_own"><MyTasks /></Protected>} />
        <Route path="task-admin" element={<Protected anyPerm={["task:view_all", "task:manage"]}><TaskAdmin /></Protected>} />
        <Route path="users" element={<Protected anyPerm={["user:view","role:view"]}><Users /></Protected>} />
        <Route path="marketplace-orders" element={<Protected perm="marketplace:view"><MarketplaceOrders /></Protected>} />
        <Route path="marketplace-mappings" element={<Protected perm="marketplace:manage"><MarketplaceSkuMapping /></Protected>} />
        <Route path="marketplace-products" element={<Protected perm="marketplace:products_view"><MarketplaceProducts /></Protected>} />
        <Route path="marketplace-reports" element={<Protected perm="marketplace:reports_view"><MarketplaceReports /></Protected>} />
        <Route path="marketplace-erp" element={<Protected anyPerm={["marketplace:fulfillment_view","marketplace:returns_view","marketplace:finance_view","marketplace:cs_view"]}><MarketplaceErpReads /></Protected>} />
        <Route path="marketplace-connections" element={<Protected perm="marketplace:manage"><MarketplaceConnections /></Protected>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
